import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle, TrendingUp, TrendingDown, ArrowUp, ArrowDown, Activity } from 'lucide-react';
import { getApiUrl } from '@/config/api';
import { authenticatedFetch } from '@/utils/apiUtils';

interface SymmetryFlag {
    athlete_name: string;
    athlete_id: string;
    metric: string;
    metric_key?: string;
    test_id?: string;
    severity: 'yellow' | 'red';
    magnitude_pct: number;
    favored_side: 'L' | 'R' | null;
    /** @deprecated API may still send signed % */
    value?: number;
}

interface TrendItem {
    athlete_name: string;
    athlete_id: string;
    metric: string;
    metric_key?: string;
    test_id?: string;
    type: string;
    detail: string;
    sentiment: 'positive' | 'negative' | 'neutral';
    lookback_n?: number;
    current_display?: string;
    avg_display?: string;
    pct_change?: number;
    prev_display?: string;
    pct_vs_prev?: number | null;
    record_ref?: string;
}

interface BriefingData {
    symmetry: {
        status: 'ok' | 'warning' | 'alert';
        flags: SymmetryFlag[];
    };
    trends: TrendItem[];
}

export interface BriefingSelection {
    athleteId: string;
    metricKey?: string;
    testId?: string;
}

interface TestBriefingProps {
    testTypeId: string;
    testDate?: string;
    onSelectAthlete?: (selection: BriefingSelection) => void;
}

/* ── Card types ─────────────────────────────────────────────── */

type StructuredTrendCard = {
    kind: 'structured';
    key: string;
    athleteId: string;
    metricKey?: string;
    testId?: string;
    name: string;
    metric: string;
    pct: number;
    accentClass: string;
    arrowUp: boolean;
    arrowDown: boolean;
    oldVal: string;
    newVal: string;
    oldTooltip: string;
    cardType: 'trend' | 'record';
    recordLabel?: string;
};

type FallbackTrendCard = {
    kind: 'fallback';
    key: string;
    athleteId: string;
    metricKey?: string;
    testId?: string;
    name: string;
    metric: string;
    detail: string;
};

type TrendCard = StructuredTrendCard | FallbackTrendCard;

/* ── Helpers ────────────────────────────────────────────────── */

function parseMetricDisplay(s: string): number | null {
    const m = s.trim().match(/^-?\d+(?:\.\d+)?/);
    if (!m) return null;
    const v = parseFloat(m[0]);
    return Number.isFinite(v) ? v : null;
}

function pctFromDisplays(prevStr: string, curStr: string): number | null {
    const prevN = parseMetricDisplay(prevStr);
    const curN = parseMetricDisplay(curStr);
    if (prevN === null || curN === null) return null;
    if (Math.abs(prevN) < 1e-12) {
        if (Math.abs(curN) < 1e-12) return 0;
        return curN > prevN ? 100 : -100;
    }
    return Math.round(((curN - prevN) / Math.abs(prevN)) * 100);
}

function parseRecordFromDetail(detail: string): {
    current_display: string;
    prev_display: string;
    record_ref: 'prev low' | 'prev high' | 'prev best';
} | null {
    const low = detail.match(/^New low:\s*(.+?)\s*\(prev low\s+(.+?)\)\s*$/i);
    if (low) return { current_display: low[1].trim(), prev_display: low[2].trim(), record_ref: 'prev low' };
    const high = detail.match(/^New high:\s*(.+?)\s*\(prev high\s+(.+?)\)\s*$/i);
    if (high) return { current_display: high[1].trim(), prev_display: high[2].trim(), record_ref: 'prev high' };
    const best = detail.match(/^New best:\s*(.+?)\s*\(prev\s+(.+?)\)\s*$/i);
    if (best) return { current_display: best[1].trim(), prev_display: best[2].trim(), record_ref: 'prev best' };
    return null;
}

function trendToCard(t: TrendItem, i: number): TrendCard {
    const key = `${t.athlete_id}-${t.metric}-${t.type}-${i}`;
    const base = { athleteId: t.athlete_id, metricKey: t.metric_key, testId: t.test_id, name: t.athlete_name, metric: t.metric };

    if (
        t.lookback_n != null &&
        t.current_display != null &&
        t.avg_display != null &&
        t.pct_change != null
    ) {
        const pct = t.pct_change;
        const accent =
            t.sentiment === 'positive'
                ? 'text-green-600 dark:text-green-400'
                : t.sentiment === 'negative'
                  ? 'text-red-600 dark:text-red-400'
                  : 'cgm-text-secondary';
        return {
            kind: 'structured', key, ...base, pct,
            accentClass: accent,
            arrowUp: pct > 0, arrowDown: pct < 0,
            oldVal: t.avg_display, newVal: t.current_display,
            oldTooltip: `Average of last ${t.lookback_n} tests`,
            cardType: 'trend' as const,
        };
    }

    let recordRef = t.record_ref as 'prev low' | 'prev high' | 'prev best' | undefined;
    let currentDisplay = t.current_display ?? null;
    let prevDisplay = t.prev_display ?? null;

    if (
        (!recordRef || !currentDisplay || !prevDisplay) &&
        (t.type === 'personal_worst' || t.type === 'personal_best')
    ) {
        const parsed = parseRecordFromDetail(t.detail);
        if (parsed) {
            recordRef = parsed.record_ref;
            currentDisplay = currentDisplay ?? parsed.current_display;
            prevDisplay = prevDisplay ?? parsed.prev_display;
        }
    }

    if (recordRef && currentDisplay != null && prevDisplay != null) {
        let pct: number | null = typeof t.pct_vs_prev === 'number' ? t.pct_vs_prev : null;
        if (pct === null) pct = pctFromDisplays(prevDisplay, currentDisplay);
        if (pct === null) return { kind: 'fallback', key, ...base, detail: t.detail };

        const accent = t.type === 'personal_best'
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400';

        let arrowUp = pct > 0;
        let arrowDown = pct < 0;
        if (recordRef === 'prev low') { arrowUp = false; arrowDown = true; }
        else if (recordRef === 'prev high') { arrowUp = true; arrowDown = false; }

        const refTooltips: Record<string, string> = { 'prev low': 'Previous low', 'prev high': 'Previous high', 'prev best': 'Previous best' };
        const recordLabels: Record<string, string> = { 'prev low': 'New low', 'prev high': 'New high' };

        return {
            kind: 'structured', key, ...base, pct,
            accentClass: accent, arrowUp, arrowDown,
            oldVal: prevDisplay, newVal: currentDisplay,
            oldTooltip: refTooltips[recordRef] ?? `Compared to ${recordRef}`,
            cardType: 'record' as const,
            recordLabel: recordLabels[recordRef] ?? 'Record',
        };
    }

    return { kind: 'fallback', key, ...base, detail: t.detail };
}

/* ── Shared card renderer ───────────────────────────────────── */

function TrendCardItem({
    c,
    onClick,
}: {
    c: TrendCard;
    onClick: () => void;
}) {
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
            className="overflow-visible border border-[var(--card-border)] bg-[var(--bg-card)] p-3 text-left cursor-pointer transition-shadow hover:shadow-md hover:border-[var(--card-hover-border)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-ui-focus)]"
        >
            {c.kind === 'structured' ? (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-bold cgm-text-primary leading-tight">{c.name}</p>
                        {c.cardType === 'record' && c.recordLabel && (
                            <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 ${
                                c.accentClass.includes('green')
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                                {c.recordLabel}
                            </span>
                        )}
                    </div>
                    <div className={`flex items-center gap-1.5 ${c.accentClass}`}>
                        {c.cardType === 'trend' ? (
                            <>
                                {c.arrowUp && <TrendingUp size={16} strokeWidth={2.5} className="shrink-0" />}
                                {c.arrowDown && <TrendingDown size={16} strokeWidth={2.5} className="shrink-0" />}
                                {!c.arrowUp && !c.arrowDown && <Activity size={16} className="shrink-0 opacity-70" />}
                            </>
                        ) : (
                            <>
                                {c.arrowUp && <ArrowUp size={16} strokeWidth={2.5} className="shrink-0" />}
                                {c.arrowDown && <ArrowDown size={16} strokeWidth={2.5} className="shrink-0" />}
                                {!c.arrowUp && !c.arrowDown && <Activity size={16} className="shrink-0 opacity-70" />}
                            </>
                        )}
                        <span className="text-xs font-medium leading-tight">{c.metric}</span>
                    </div>
                    <p className={`text-2xl font-bold tabular-nums leading-none ${c.accentClass}`}>
                        {c.pct > 0 ? '+' : ''}{c.pct}%
                    </p>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs cgm-text-secondary">
                        <span
                            title={c.oldTooltip}
                            className="group/old relative z-10 inline-block max-w-full cursor-help border-b border-dotted border-gray-400 dark:border-gray-500"
                        >
                            <span className="break-all">{c.oldVal}</span>
                            <span className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1.5 hidden w-max max-w-[min(260px,calc(100vw-2rem))] -translate-x-1/2 bg-gray-900 px-2 py-1.5 text-left text-[11px] font-normal normal-case leading-snug tracking-normal text-white shadow-lg group-hover/old:block dark:bg-gray-700">
                                {c.oldTooltip}
                            </span>
                        </span>
                        <span className="shrink-0 tabular-nums">→</span>
                        <span className="font-bold break-all cgm-text-primary tabular-nums">{c.newVal}</span>
                    </div>
                </div>
            ) : (
                <>
                    <p className="text-xs font-bold cgm-text-primary leading-tight mb-1">{c.name}</p>
                    <p className="text-xs cgm-text-secondary mb-1">{c.metric}</p>
                    <p className="text-sm cgm-text-secondary leading-snug">{c.detail}</p>
                </>
            )}
        </div>
    );
}

/* ── Grid wrapper (shared between sections) ─────────────────── */

const CARD_GRID_CLASS =
    'grid grid-cols-1 min-[480px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-2.5';

/* ── Main component ─────────────────────────────────────────── */

const TestBriefing: React.FC<TestBriefingProps> = ({ testTypeId, testDate, onSelectAthlete }) => {
    const [data, setData] = useState<BriefingData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchData = useCallback(async (url: string) => {
        const result = await authenticatedFetch(getApiUrl(url), { cache: 'no-store' }, 'Test Briefing');
        if (!result.success) throw new Error(result.error || 'Request failed');
        return result.data;
    }, []);

    useEffect(() => {
        if (!testTypeId) return;
        setLoading(true);
        setError('');
        const dateParam = testDate ? `&test_date=${testDate}` : '&days=90';
        fetchData(`/api/hawkin/briefing/?type_id=${testTypeId}${dateParam}`)
            .then((d) => { if (d) setData(d); })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [testTypeId, testDate, fetchData]);

    if (loading) {
        return (
            <div className="cgm-card p-6">
                <div className="flex items-center justify-center h-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 cgm-spinner" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="cgm-card p-4">
                <p className="text-sm text-red-500">Briefing: {error}</p>
            </div>
        );
    }
    if (!data) return null;

    const { symmetry, trends } = data;
    const hasContent = symmetry.flags.length > 0 || trends.length > 0;
    if (!hasContent && symmetry.status === 'ok') {
        return (
            <div className="cgm-card p-4">
                <div className="flex items-center gap-2 text-sm cgm-text-secondary">
                    <CheckCircle size={16} className="text-green-500" />
                    <span>All athletes within normal ranges. No flags or notable trends.</span>
                </div>
            </div>
        );
    }

    /* ── Symmetry cards ─────────────────────────────────────── */

    const symmetryCards = symmetry.flags.map((f, i) => {
        const mag = f.magnitude_pct ?? (typeof f.value === 'number' ? Math.abs(f.value) : 0);
        const side =
            f.favored_side ??
            (typeof f.value === 'number' ? (f.value > 0 ? 'R' : f.value < 0 ? 'L' : null) : null);
        const isRed = f.severity === 'red';
        return (
            <div
                key={`${f.athlete_id}-${f.metric}-${i}`}
                role="button"
                tabIndex={0}
                onClick={() => onSelectAthlete?.({ athleteId: f.athlete_id, metricKey: f.metric_key, testId: f.test_id })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectAthlete?.({ athleteId: f.athlete_id, metricKey: f.metric_key, testId: f.test_id }); } }}
                className={`overflow-visible border p-3 text-left cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-ui-focus)] ${
                    isRed
                        ? 'border-red-300 dark:border-red-700 bg-red-50/80 dark:bg-red-900/20 hover:border-red-400 dark:hover:border-red-600'
                        : 'border-amber-300 dark:border-amber-700 bg-amber-50/80 dark:bg-amber-900/20 hover:border-amber-400 dark:hover:border-amber-600'
                }`}
            >
                <div className="flex flex-col gap-2">
                    <p className="text-xs font-bold cgm-text-primary leading-tight">{f.athlete_name}</p>
                    <div className={`flex items-center gap-1.5 ${isRed ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        <AlertTriangle size={16} strokeWidth={2.5} className="shrink-0" />
                        <span className="text-xs font-medium leading-tight">{f.metric}</span>
                    </div>
                    <p className={`text-2xl font-bold tabular-nums leading-none ${isRed ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        {mag}%
                        {side && <span className="text-base ml-1">{side}</span>}
                    </p>
                </div>
            </div>
        );
    });

    /* ── Trend cards — split into records vs trends ──────────── */

    const allCards = trends
        .map((t, i) => trendToCard(t, i))
        .filter((c) => !(c.kind === 'structured' && c.pct === 0));

    const recordCards = allCards.filter(
        (c) => c.kind === 'structured' && c.cardType === 'record'
    );
    const trendCards = allCards.filter(
        (c) => !(c.kind === 'structured' && c.cardType === 'record')
    );

    return (
        <div className="space-y-6">
            {/* Symmetry Warnings */}
            <div className="cgm-card p-5">
                <h3 className="text-sm font-semibold cgm-text-primary mb-1 flex items-center gap-2">
                    {symmetry.status === 'ok' ? (
                        <CheckCircle size={16} className="text-green-500" />
                    ) : symmetry.status === 'alert' ? (
                        <AlertTriangle size={16} className="text-red-500" />
                    ) : (
                        <AlertTriangle size={16} className="text-amber-500" />
                    )}
                    Symmetry Warnings
                </h3>
                <p className="text-xs cgm-text-secondary mb-3">
                    ≥5% asymmetry favoring one side is yellow; ≥10% is red. L = left-biased, R = right-biased.
                </p>

                {symmetry.flags.length === 0 ? (
                    <p className="text-sm cgm-text-secondary flex items-center gap-1.5">
                        <CheckCircle size={14} className="text-green-500" />
                        All athletes have proper symmetry (&lt;5% asymmetry)
                    </p>
                ) : (
                    <div className={CARD_GRID_CLASS}>
                        {symmetryCards}
                    </div>
                )}
            </div>

            {/* New Records */}
            {recordCards.length > 0 && (
                <div className="cgm-card p-5">
                    <h3 className="text-sm font-semibold cgm-text-primary mb-3 flex items-center gap-2">
                        <ArrowUp size={16} className="text-blue-500" />
                        New Records
                    </h3>
                    <div className={CARD_GRID_CLASS}>
                        {recordCards.map((c) => (
                            <TrendCardItem
                                key={c.key}
                                c={c}
                                onClick={() => onSelectAthlete?.({ athleteId: c.athleteId, metricKey: c.metricKey, testId: c.testId })}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Notable Trends */}
            {trendCards.length > 0 && (
                <div className="cgm-card p-5">
                    <h3 className="text-sm font-semibold cgm-text-primary mb-3 flex items-center gap-2">
                        <TrendingUp size={16} className="text-blue-500" />
                        Notable Trends
                    </h3>
                    <div className={CARD_GRID_CLASS}>
                        {trendCards.map((c) => (
                            <TrendCardItem
                                key={c.key}
                                c={c}
                                onClick={() => onSelectAthlete?.({ athleteId: c.athleteId, metricKey: c.metricKey, testId: c.testId })}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Empty state when no records and no trends */}
            {recordCards.length === 0 && trendCards.length === 0 && trends.length === 0 && symmetry.flags.length === 0 && (
                <div className="cgm-card p-4">
                    <p className="text-sm cgm-text-secondary">
                        No significant trends detected in recent tests.
                    </p>
                </div>
            )}
        </div>
    );
};

export default TestBriefing;
